import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Minus, Plus } from 'lucide-react'
import apiClient from '@/api/client'
import type { FromSuggestionsResult, OrderSuggestion } from '@/types/kitchenFlowPurchasing'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { fmtQty } from '@/lib/quantity'
import { cn } from '@/lib/utils'
import { fromSuggestionsOutcome, groupPicksByVendor } from './purchasingLogic'
import { poLineQtyText } from './purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.suggestions

interface Pick { on: boolean; qty: string }

/** Kitchen flow lane D: "What to buy now" → one tap makes a draft PO per
 *  vendor through the normal PO path (same approval rule as New order). */
export function OrderSuggestionsDialog({ open, onClose, onOpenPo, showToast }: {
  open: boolean
  onClose: () => void
  onOpenPo: (id: string) => void
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['orderSuggestions'],
    queryFn: () => apiClient.getOrderSuggestions().then((r) => ({ rows: r.data ?? [], unsized: r.unsized_count ?? 0 })),
    enabled: open,
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
  })
  const rows: OrderSuggestion[] = data?.rows ?? []
  const unsized = data?.unsized ?? 0
  const [picks, setPicks] = useState<Record<string, Pick>>({})
  // Seed the picks once per opening, from the FRESH list — never from a
  // cached one, and never again mid-edit when a background refetch lands.
  const [seeded, setSeeded] = useState(false)
  const isLoading = !seeded
  const [result, setResult] = useState<FromSuggestionsResult | null>(null)
  const idemKey = useMemo(() => (open ? crypto.randomUUID() : ''), [open])

  useEffect(() => {
    if (!open) {
      setResult(null)
      setPicks({})
      setSeeded(false)
      return
    }
    if (!seeded && data && !isFetching) {
      setPicks(Object.fromEntries(data.rows.map((r) => [r.stock_item_id, { on: true, qty: String(r.suggested_qty) }])))
      setSeeded(true)
    }
  }, [open, data, isFetching, seeded])

  const chosen = rows
    .filter((r) => picks[r.stock_item_id]?.on && Number(picks[r.stock_item_id]?.qty) > 0)
    .map((r) => ({ stock_item_id: r.stock_item_id, qty: Number(picks[r.stock_item_id].qty), supplier_id: r.supplier_id }))
  const { byVendor } = groupPicksByVendor(chosen)

  const make = useMutation({
    mutationFn: () =>
      apiClient.createPurchaseOrdersFromSuggestions({
        lines: chosen.map((c) => ({ stock_item_id: c.stock_item_id, qty: c.qty })),
        idempotency_key: idemKey || undefined,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['orderSuggestions'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      const out = fromSuggestionsOutcome(res.data)
      if (out.kind === 'replay') {
        // The first tap already made them (its answer was lost): say so and close.
        showToast('success', S.alreadyMade)
        onClose()
        return
      }
      setResult(out.result)
    },
    onError: (e: Error) => showToast('error', e.message),
  })

  // Group rows by vendor for display; no-vendor rows last.
  const groups = useMemo(() => {
    const m = new Map<string, OrderSuggestion[]>()
    for (const r of rows) {
      const k = r.supplier_name ?? ''
      m.set(k, [...(m.get(k) ?? []), r])
    }
    return [...m.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
  }, [rows])

  const setPick = (id: string, patch: Partial<Pick>) => setPicks((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  if (open && result) {
    const pending = result.purchase_orders.some((p) => p.status === 'pending_approval')
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{S.made(result.purchase_orders.length)}</DialogTitle>
            <DialogDescription>
              {result.purchase_orders.length > 0 ? (pending ? S.needsApproval : S.readyToSend) : null}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {result.purchase_orders.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="flex min-h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm hover:bg-muted/40"
                  onClick={() => { onClose(); onOpenPo(p.id) }}
                >
                  <span className="font-medium">{p.supplier_name}</span>
                  <span className="text-sm text-muted-foreground">{p.line_count} item{p.line_count === 1 ? '' : 's'}</span>
                </button>
              </li>
            ))}
          </ul>
          {result.unassigned.length > 0 && (
            <div className="rounded-lg border border-warning-border bg-warning-surface/40 p-3 text-sm">
              <p className="font-medium">{S.unassigned}</p>
              <ul className="mt-1 list-disc pl-5">
                {result.unassigned.map((u) => <li key={u.stock_item_id}>{u.name} — {fmtQty(u.qty)} {u.unit}</li>)}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{S.title}</DialogTitle>
          <DialogDescription>{S.help}</DialogDescription>
        </DialogHeader>
        {isLoading && isError && !isFetching ? (
          // Pilot Batch 5: a failed load says so (it used to stay on "Loading…").
          <div role="alert" className="space-y-3 rounded-lg border p-6 text-center text-sm">
            <p className="text-danger-fg">{PURCHASING_STRINGS.errors.suggestions}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>{PURCHASING_STRINGS.errors.retry}</Button>
          </div>
        ) : isLoading ? (
          <p className="py-8 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="space-y-2 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            <p>{S.empty}</p>
            {unsized > 0 && <p className="font-medium text-foreground">{S.unsized(unsized)}</p>}
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map(([vendor, items]) => (
              <section key={vendor || 'none'}>
                <h3 className={cn('mb-2 text-sm font-semibold', !vendor && 'text-warning-fg')}>
                  {vendor ? `${S.vendor} ${vendor}` : S.noVendor}
                </h3>
                <ul className="space-y-2">
                  {items.map((r) => {
                    const p = picks[r.stock_item_id] ?? { on: true, qty: String(r.suggested_qty) }
                    const qty = Number(p.qty) || 0
                    const price = r.last_price ?? r.default_unit_cost
                    // Pilot Batch 5: the vendor sells whole packs — step by a
                    // pack and say "2 bags (50 kg)".
                    const pack = r.pack_qty && r.pack_qty > 0 ? { label: r.pack_label ?? 'pack', qty: r.pack_qty } : null
                    const step = pack ? pack.qty : 1
                    return (
                      <li key={r.stock_item_id} className={cn('rounded-lg border p-3', !p.on && 'opacity-60')}>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={p.on} aria-label={r.name}
                            onChange={(e) => setPick(r.stock_item_id, { on: e.target.checked })}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold">{r.name}</p>
                            <p className="text-sm text-muted-foreground tabular-nums">
                              {S.now} {fmtQty(r.basis === 'par' ? r.store_on_hand : r.quantity_on_hand)} {r.unit}
                              {r.on_order_qty > 0 ? ` · ${S.onOrder} ${fmtQty(r.on_order_qty)}` : ''}
                              {' · '}{r.basis === 'par' ? S.byPar : S.byReorder}
                            </p>
                            {r.pack_label && <p className="text-xs text-muted-foreground">{r.pack_label}{r.pack_qty ? ` = ${fmtQty(r.pack_qty)} ${r.unit}` : ''}</p>}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <Button type="button" variant="outline" className="h-10 w-10 p-0" aria-label="Less" disabled={!p.on}
                              onClick={() => setPick(r.stock_item_id, { qty: String(Math.max(0, Math.round((qty - step) * 1000) / 1000)) })}>
                              <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                              inputMode="decimal" aria-label={`${S.buy} ${r.name}`} disabled={!p.on}
                              className="w-20 text-center font-semibold tabular-nums" value={p.qty}
                              onChange={(e) => setPick(r.stock_item_id, { qty: e.target.value })}
                            />
                            <Button type="button" variant="outline" className="h-10 w-10 p-0" aria-label="More" disabled={!p.on}
                              onClick={() => setPick(r.stock_item_id, { qty: String(Math.round((qty + step) * 1000) / 1000) })}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {pack && qty > 0 && (
                          <p className="mt-1 text-right text-sm font-medium tabular-nums">{poLineQtyText(qty, r.unit, pack)}</p>
                        )}
                        {price != null && qty > 0 && (
                          <p className="mt-1 text-right text-xs text-muted-foreground tabular-nums">≈ {formatCurrency(price * qty)}</p>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))}
            {unsized > 0 && <p className="text-sm text-muted-foreground">{S.unsized(unsized)}</p>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={chosen.length === 0 || make.isPending} onClick={() => make.mutate()}>
            {make.isPending ? 'Making…' : S.make(byVendor.size)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
